import type {
  ExcelSheetCell,
  ExcelSheetLayout,
  ExportModel,
  ExportNotesSection,
  ExportSelection,
  Noyau,
} from '@mss_creator/shared';
import {
  EXCEL_FONT_FAMILY,
  EXCEL_FONT_SIZE_PT,
  EXCEL_PT_PER_PX,
  applyColumnOrder,
  buildExcelNotesSheetLayout,
  buildExcelSheetLayout,
} from '@mss_creator/shared';
import { type CSSProperties, type ReactNode, useState } from 'react';
import {
  BORDER_WIDTH_PX,
  CHROME_CLASS,
  GUTTER_WIDTH_PX,
  LETTER_ROW_HEIGHT_PX,
  columnLetters,
} from './excel-preview-chrome';
import type { ExportOptionsState } from './export-customization';
import { ExportExcelNotesSheet } from './export-excel-notes-sheet';

export interface ExportExcelPreviewProps {
  model: ExportModel;
  /** The route's real selection — the levier-name mode decides the label columns and merges. */
  selection: ExportSelection;
  /** The two option blocks an Excel request carries (decision X1) — no dressing. */
  options: Pick<ExportOptionsState, 'table' | 'appearance'>;
  noyau: Noyau;
  /** Raw per-column pixel widths — `matrix.viewState.columnWidths`, under the table's own overrides. */
  columnWidths: Record<string, number>;
  /** Raw per-row pixel heights — `matrix.viewState.rowHeights`, read in `ecran` mode only (decision X4). */
  rowHeights: Record<string, number>;
  /** The « Notes » sheet the file will carry (issue #19), `null` when the option is off or there is nothing to print. */
  notes?: ExportNotesSection | null;
}

type SheetId = 'matrice' | 'notes';

interface CellSpan {
  rowSpan: number;
  colSpan: number;
}

/**
 * The layout's merges as table spans: the master cell (top-left) carries the
 * span, every other cell of the range is covered and not rendered at all.
 */
function mergeSpans(layout: ExcelSheetLayout): {
  spans: Map<string, CellSpan>;
  covered: Set<string>;
} {
  const spans = new Map<string, CellSpan>();
  const covered = new Set<string>();
  for (const merge of layout.merges) {
    spans.set(`${merge.top}:${merge.left}`, {
      rowSpan: merge.bottom - merge.top + 1,
      colSpan: merge.right - merge.left + 1,
    });
    for (let row = merge.top; row <= merge.bottom; row += 1) {
      for (let column = merge.left; column <= merge.right; column += 1) {
        if (row !== merge.top || column !== merge.left) covered.add(`${row}:${column}`);
      }
    }
  }
  return { spans, covered };
}

/**
 * The Excel export's indicative preview (issue #12, spec F1): the shared
 * `buildExcelSheetLayout` — the very description the backend's exceljs writer
 * consumes — drawn as a spreadsheet: column letters, row numbers, gridlines,
 * the frozen header rows and leading columns held by CSS sticky (none when the
 * layout freezes nothing), the layout's document colours inline and Titillium
 * at the sheet's size. Both table structures (issues #29, #30) are the same
 * drawing: the layout carries the rows, merges, frozen split and blank rows,
 * this component knows nothing of the transposition.
 *
 * It lays the grid out as the backend's excel branch does (decision X9): the
 * model in `table.columnOrder`, the screen widths under `table.columnWidths`.
 * « Indicatif » because xlsx cannot embed the font and Excel wraps on its own
 * metrics — the banner says so. No annotation or « Aménager » control here.
 */
export function ExportExcelPreview({
  model,
  selection,
  options,
  noyau,
  columnWidths,
  rowHeights,
  notes = null,
}: ExportExcelPreviewProps) {
  const [activeSheet, setActiveSheet] = useState<SheetId>('matrice');
  const layout = buildExcelSheetLayout({
    model: applyColumnOrder(model, options.table.columnOrder),
    activationLevierNames: selection.activationLevierNames,
    table: options.table,
    appearance: options.appearance,
    noyau,
    columnWidths: { ...columnWidths, ...options.table.columnWidths },
    viewStateRowHeights: rowHeights,
  });
  const notesLayout =
    notes === null
      ? null
      : buildExcelNotesSheetLayout({ section: notes, appearance: options.appearance });
  // The tab survives the notes going away (option unticked): back on the matrix.
  const shownSheet: SheetId = activeSheet === 'notes' && notesLayout !== null ? 'notes' : 'matrice';
  const { spans, covered } = mergeSpans(layout);

  const headerCount = layout.headerRows.length;
  const sheetRows = [...layout.headerRows, ...layout.rows];

  // Sticky offsets: the frozen columns sit right of the row-number gutter, the
  // header rows below the column-letter row and below each other.
  const columnLefts: number[] = [];
  let nextLeft = GUTTER_WIDTH_PX;
  for (const column of layout.columns.slice(0, layout.frozen.xSplit)) {
    columnLefts.push(nextLeft);
    nextLeft += column.widthPx;
  }
  const headerTops: number[] = [];
  let nextTop = LETTER_ROW_HEIGHT_PX;
  for (const row of layout.headerRows) {
    headerTops.push(nextTop);
    nextTop += row.heightPt / EXCEL_PT_PER_PX;
  }
  const tableWidthPx =
    GUTTER_WIDTH_PX + layout.columns.reduce((total, column) => total + column.widthPx, 0);

  const edge = `${BORDER_WIDTH_PX[layout.border.style]}px solid ${layout.border.hex}`;
  const gridlines: CSSProperties = { borderRight: edge, borderBottom: edge };
  /** A blank row carries no border: only the sheet's own faint gridlines show. */
  const blankGridlines: CSSProperties = {
    borderRight: '1px solid var(--color-gray-100)',
    borderBottom: '1px solid var(--color-gray-100)',
  };

  /** A merged cell clips at the rows it spans together, like any fixed-height Excel cell. */
  const spannedHeightPt = (rowIndex: number, rowSpan: number): number =>
    sheetRows.slice(rowIndex, rowIndex + rowSpan).reduce((total, row) => total + row.heightPt, 0);

  /**
   * One document cell at 0-based sheet indices, unless a merge covers it. The
   * frozen split decides what sticks: the header rows to the top, the leading
   * columns to the left, and a cell in both stays above the rest.
   */
  const sheetCell = (
    source: ExcelSheetCell,
    rowIndex: number,
    columnIndex: number,
    blank = false,
  ): ReactNode => {
    const key = `${rowIndex + 1}:${columnIndex + 1}`;
    if (covered.has(key)) return null;
    const span = spans.get(key);
    const { style } = source;
    const left = columnLefts[columnIndex];
    const top = headerTops[rowIndex];
    // A header cell outside the frozen columns names what lies below it; the
    // frozen ones are the corner, an empty label cell.
    const Tag = top !== undefined && left === undefined ? 'th' : 'td';
    const text: CSSProperties = {
      fontSize: `${style.sizePt}pt`,
      fontWeight: style.bold ? 700 : 400,
    };
    let zIndex: number | undefined;
    if (top !== undefined) zIndex = left === undefined ? 2 : 3;
    else if (left !== undefined) zIndex = 1;
    return (
      <Tag
        key={key}
        {...(Tag === 'th' ? { scope: 'col' as const } : {})}
        rowSpan={span?.rowSpan}
        colSpan={span?.colSpan}
        className={`px-1 text-left align-top${style.fillHex === null ? ' bg-white' : ''}`}
        style={{
          ...(blank ? blankGridlines : gridlines),
          ...text,
          ...(zIndex === undefined ? {} : { position: 'sticky', zIndex }),
          ...(left === undefined ? {} : { left }),
          ...(top === undefined ? {} : { top }),
          ...(style.fillHex === null ? {} : { backgroundColor: style.fillHex }),
          ...(style.fontHex === null ? {} : { color: style.fontHex }),
        }}
      >
        {/* A fixed-height Excel row clips its content — so does the preview. */}
        <div
          className="whitespace-pre-wrap"
          style={{
            ...text,
            maxHeight: `${spannedHeightPt(rowIndex, span?.rowSpan ?? 1)}pt`,
            overflow: 'hidden',
          }}
        >
          {source.text}
        </div>
      </Tag>
    );
  };

  const chrome = CHROME_CLASS;

  return (
    <div className="relative flex h-full min-h-0 flex-col gap-2">
      <p
        role="note"
        className="rounded-md bg-bleu-ciel-tint px-3 py-2 text-bleu-ciel-deep text-small"
      >
        Aperçu indicatif — ouvrez dans Excel pour le rendu exact.
      </p>
      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-gray-200 bg-white">
        {shownSheet === 'notes' && notesLayout !== null ? (
          <ExportExcelNotesSheet layout={notesLayout} />
        ) : (
          <table
            aria-label="Aperçu Excel"
            className="border-separate border-spacing-0"
            style={{
              tableLayout: 'fixed',
              width: tableWidthPx,
              fontFamily: `"${EXCEL_FONT_FAMILY}", sans-serif`,
              fontSize: `${EXCEL_FONT_SIZE_PT}pt`,
            }}
          >
            <colgroup>
              <col style={{ width: GUTTER_WIDTH_PX }} />
              {layout.columns.map((column, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: sheet columns are identified by their position
                <col key={index} style={{ width: column.widthPx }} />
              ))}
            </colgroup>
            <thead>
              <tr style={{ height: LETTER_ROW_HEIGHT_PX }}>
                <th
                  aria-hidden="true"
                  className={chrome}
                  style={{ position: 'sticky', top: 0, left: 0, zIndex: 4 }}
                />
                {layout.columns.map((_, index) => (
                  <th
                    // biome-ignore lint/suspicious/noArrayIndexKey: sheet columns are identified by their position
                    key={index}
                    scope="col"
                    className={chrome}
                    style={
                      index < layout.frozen.xSplit
                        ? { position: 'sticky', left: columnLefts[index], top: 0, zIndex: 3 }
                        : { position: 'sticky', top: 0, zIndex: 2 }
                    }
                  >
                    {columnLetters(index)}
                  </th>
                ))}
              </tr>
              {layout.headerRows.map((row, rowIndex) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: header rows are identified by their position
                <tr key={rowIndex} style={{ height: `${row.heightPt}pt` }}>
                  <th
                    scope="row"
                    className={chrome}
                    style={{ position: 'sticky', top: headerTops[rowIndex], left: 0, zIndex: 3 }}
                  >
                    {rowIndex + 1}
                  </th>
                  {row.cells.map((source, columnIndex) => sheetCell(source, rowIndex, columnIndex))}
                </tr>
              ))}
            </thead>
            <tbody>
              {layout.rows.map((row, index) => (
                <tr key={row.key} style={{ height: `${row.heightPt}pt` }}>
                  <th
                    scope="row"
                    className={chrome}
                    style={{ position: 'sticky', left: 0, zIndex: 1 }}
                  >
                    {headerCount + index + 1}
                  </th>
                  {row.cells.map((source, columnIndex) =>
                    sheetCell(source, headerCount + index, columnIndex, row.kind === 'blank'),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {notesLayout !== null && (
        <div role="tablist" aria-label="Feuilles du classeur" className="flex shrink-0 gap-1">
          {(
            [
              ['matrice', 'Matrice'],
              ['notes', 'Notes'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={shownSheet === id}
              onClick={() => setActiveSheet(id)}
              className={`rounded-md px-3 py-1 text-small ${shownSheet === id ? 'bg-bleu-nuit-tint font-semibold text-bleu-nuit' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
